import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { AutoCarousel } from '../components/ui/auto-carousel';
import { HorizontalCarousel } from '../components/ui/horizontal-carousel';
import { WhatsAppCTA } from '../components/ui/whatsapp-cta';
import { AppointmentForm } from '../components/ui/appointment-form';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { API_BASE } from '../config/api';
import { ensureAbsoluteUrl, truncateText } from '../utils/socialMeta';

export const UniversityDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [university, setUniversity] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProgramLevel, setSelectedProgramLevel] = useState('');
  const [selectedField, setSelectedField] = useState('');
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  const [showAllScholarships, setShowAllScholarships] = useState(false);
  const [programsSectionExpanded, setProgramsSectionExpanded] = useState(true);
  const [scholarshipsSectionExpanded, setScholarshipsSectionExpanded] = useState(true);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);

  const backendUrl = API_BASE;

  // Fetch university details
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch university by slug
        const uniResponse = await fetch(`${backendUrl}/api/public/universities/${slug}`);
        if (!uniResponse.ok) throw new Error('University not found');
        const uniData = await uniResponse.json();
        const universityData = uniData.university;
        setUniversity(universityData);
        
        // Parallel fetch: programs and scholarships using university ID
        const universityId = universityData.id;
        const [progsResponse, scholsResponse] = await Promise.all([
          fetch(`${backendUrl}/api/public/programs?university_id=${universityId}`),
          fetch(`${backendUrl}/api/public/scholarships?university_id=${universityId}`)
        ]);
        
        // Process programs
        if (progsResponse.ok) {
          const progsData = await progsResponse.json();
          setPrograms(progsData.programs || []);
        }
        
        // Process scholarships
        if (scholsResponse.ok) {
          const scholsData = await scholsResponse.json();
          setScholarships(scholsData.scholarships || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [slug, backendUrl]);

  const handleConsultClick = () => {
    window.open('https://wa.me/60123456789', '_blank');
  };

  const handleBookTourClick = () => {
    setShowAppointmentForm(true);
  };

  const handleAppointmentFormClose = () => {
    setShowAppointmentForm(false);
  };

  const handleAppointmentSubmit = async (formData) => {
    try {
      const response = await fetch(`${backendUrl}/api/forms/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'appointment_booking',
          ...formData,
          source_page: window.location.href,
        }),
      });
      
      if (response.ok) {
        alert('Appointment booked successfully! We will contact you soon.');
        setShowAppointmentForm(false);
      } else {
        alert('Failed to book appointment. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting appointment:', error);
      alert('An error occurred. Please try again.');
    }
  };

  // USP images from homepage
  const uspItems = [
    { imageUrl: '/placeholders/image-generic.svg', alt: 'Expert Guidance' },
    { imageUrl: '/placeholders/image-generic.svg', alt: 'University Network' },
    { imageUrl: '/placeholders/image-generic.svg', alt: 'Scholarship Support' },
    { imageUrl: '/placeholders/image-generic.svg', alt: 'Visa Assistance' },
    { imageUrl: '/placeholders/image-generic.svg', alt: 'Application Help' },
    { imageUrl: '/placeholders/image-generic.svg', alt: 'Success Stories' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-[#4B5563]">Loading university details...</p>
        </div>
      </div>
    );
  }

  if (error || !university) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111827] mb-4">University Not Found</h1>
            <p className="text-[#4B5563] mb-6">The university you're looking for doesn't exist or has been removed.</p>
            <a 
              href="/universities" 
              data-testid="browse-universities-button"
              className="px-6 py-3 bg-[#F97316] hover:bg-[#fb7e2e] text-white font-semibold rounded-lg transition-colors inline-block focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
            >
              Browse Universities
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Prepare meta tags for university
  const uniTitle = university.seo_title || university.name || 'University';
  const uniDescription = truncateText(university.seo_description || university.short_description || `Explore ${university.name} - ${university.institution_type} in ${university.city}, ${university.country}`, 200);
  const uniImage = university.images && university.images.length > 0 
    ? university.images[0] 
    : (university.logo || '');
  const uniUrl = `https://www.unisave.my/universities/${university.slug || university.id}`;

  // Group programs by level and field
  const levels = [...new Set(programs.map(p => p.level_of_study))].filter(Boolean);
  
  // Set initial level
  if (!selectedProgramLevel && levels.length > 0) {
    setSelectedProgramLevel(levels[0]);
  }
  
  // Get fields for selected level
  const currentLevelPrograms = programs.filter(p => p.level_of_study === selectedProgramLevel);
  const fieldsForLevel = [...new Set(currentLevelPrograms.map(p => p.field_of_study || 'General'))].filter(Boolean);
  
  // Set initial field
  if (!selectedField && fieldsForLevel.length > 0) {
    setSelectedField(fieldsForLevel[0]);
  }
  
  // Get programs for selected level and field
  const filteredPrograms = showAllPrograms 
    ? currentLevelPrograms.filter(p => (p.field_of_study || 'General') === selectedField)
    : currentLevelPrograms.filter(p => (p.field_of_study || 'General') === selectedField).slice(0, 6);

  const displayedScholarships = showAllScholarships ? scholarships : scholarships.slice(0, 6);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="university-detail-page">
      {/* Open Graph & Twitter Card Meta for University */}
      <Helmet>
        <title>{uniTitle} | Unisave</title>
        <meta name="description" content={uniDescription} />
        <link rel="canonical" href={uniUrl} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={uniTitle} />
        <meta property="og:description" content={uniDescription} />
        <meta property="og:image" content={ensureAbsoluteUrl(uniImage)} />
        <meta property="og:url" content={uniUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Unisave" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={uniTitle} />
        <meta name="twitter:description" content={uniDescription} />
        <meta name="twitter:image" content={ensureAbsoluteUrl(uniImage)} />
      </Helmet>
      
      <Header />
      
      <main className="flex-1">
        {/* BREADCRUMB */}
        <div className="container mx-auto px-6 lg:px-10 max-w-[1100px] pt-4">
          <Breadcrumb items={[
            { label: 'Universities', href: '/universities' },
            { label: university.display_name || university.name, href: '#' }
          ]} />
        </div>

        {/* IMAGE GALLERY AT TOP */}
        {university.images && university.images.length > 0 && (
          <section className="w-full bg-[#F8FAFC] border-b border-[#E5E7EB]" data-testid="image-gallery-section">
            <div className="container mx-auto px-6 lg:px-10 max-w-[1100px] py-8 md:py-10">
              <div className="mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-[#111827] text-center md:text-left">Campus Gallery</h2>
              </div>
              <HorizontalCarousel itemWidth={800}>
                {university.images.map((image, idx) => (
                  <div key={idx} className="flex-shrink-0" style={{ width: '800px', height: '450px' }}>
                    <img 
                      src={image} 
                      alt={`${university.name} campus ${idx + 1}`}
                      className="w-full h-full object-cover rounded-xl shadow-sm"
                      loading={idx === 0 ? "eager" : "lazy"}
                      fetchPriority={idx === 0 ? "high" : "auto"}
                      width="800"
                      height="450"
                    />
                  </div>
                ))}
              </HorizontalCarousel>
            </div>
          </section>
        )}

        {/* SECTION 1: HERO */}
        <section className="bg-white py-10 md:py-14" data-testid="university-hero-section">
          <div className="container mx-auto px-6 lg:px-10 max-w-[1100px]">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left: University Info */}
              <div className="lg:col-span-2">
                {/* Desktop: Side by side */}
                <div className="hidden md:flex gap-4 md:gap-6 items-start">
                  {/* Logo */}
                  <div className="flex-shrink-0 w-16 h-16 md:w-24 md:h-24 rounded-xl bg-white border border-[#E5E7EB] shadow-sm flex items-center justify-center p-2 md:p-3">
                    {university.logo ? (
                      <img src={university.logo} alt={`${university.name} logo`} className="max-w-full max-h-full object-contain" />
                    ) : (
                      <svg className="w-8 h-8 md:w-12 md:h-12 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1">
                    <h1 className="text-xl md:text-3xl lg:text-4xl text-[#111827] mb-2 md:mb-3 font-bold leading-tight text-left" data-testid="university-name">
                      {university.display_name || university.name}
                    </h1>
                    <p className="text-[#4B5563] mb-2 flex items-center justify-start gap-2 text-sm md:text-base" data-testid="university-location">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      {university.city}, {university.country}
                    </p>
                    <p className="text-sm text-[#4B5563] mb-3 md:mb-4 text-left">
                      <span className="font-medium">{university.institution_type}</span>
                    </p>
                    
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 justify-start">
                      {university.featured && (
                        <span className="px-3 py-1.5 rounded-md bg-[#E0F2FE] text-[#0369A1] text-xs font-medium" data-testid="verified-badge">✓ Verified</span>
                      )}
                      {(university.partner_status === 'Close' || university.partner_status === 'Normal') && (
                        <span className="px-3 py-1.5 rounded-md bg-[#F8FAFC] text-[#111827] text-xs font-medium border border-[#E5E7EB]" data-testid="partner-badge">Partner</span>
                      )}
                      {university.qs_ranking && (
                        <span className="px-3 py-1.5 rounded-md bg-[#FEF3C7] text-[#92400E] text-xs font-medium" data-testid="ranking-badge">QS #{university.qs_ranking}</span>
                      )}
                      {university.setara_rating && (
                        <span className="px-3 py-1.5 rounded-md bg-[#FEF3C7] text-[#92400E] text-xs font-medium">SETARA {university.setara_rating}★</span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Mobile: Logo on top, name centered below */}
                <div className="md:hidden flex flex-col items-center">
                  {/* Logo */}
                  <div className="w-24 h-24 rounded-xl bg-white border border-[#E5E7EB] shadow-sm flex items-center justify-center p-3 mb-4">
                    {university.logo ? (
                      <img src={university.logo} alt={`${university.name} logo`} className="max-w-full max-h-full object-contain" />
                    ) : (
                      <svg className="w-12 h-12 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="text-center w-full">
                    <h1 className="text-2xl text-[#111827] mb-2 font-bold leading-tight" style={{ fontSize: '1.5rem' }} data-testid="university-name">
                      {university.display_name || university.name}
                    </h1>
                    <p className="text-[#4B5563] mb-2 flex items-center justify-center gap-2 text-sm" data-testid="university-location">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      {university.city}, {university.country}
                    </p>
                    <p className="text-sm text-[#4B5563] mb-3">
                      <span className="font-medium">{university.institution_type}</span>
                    </p>
                    
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 justify-center">
                      {university.featured && (
                        <span className="px-3 py-1.5 rounded-md bg-[#E0F2FE] text-[#0369A1] text-xs font-medium" data-testid="verified-badge">✓ Verified</span>
                      )}
                      {(university.partner_status === 'Close' || university.partner_status === 'Normal') && (
                        <span className="px-3 py-1.5 rounded-md bg-[#F8FAFC] text-[#111827] text-xs font-medium border border-[#E5E7EB]" data-testid="partner-badge">Partner</span>
                      )}
                      {university.qs_ranking && (
                        <span className="px-3 py-1.5 rounded-md bg-[#FEF3C7] text-[#92400E] text-xs font-medium" data-testid="ranking-badge">QS #{university.qs_ranking}</span>
                      )}
                      {university.setara_rating && (
                        <span className="px-3 py-1.5 rounded-md bg-[#FEF3C7] text-[#92400E] text-xs font-medium">SETARA {university.setara_rating}★</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right: Sticky CTA Card (Desktop) */}
              <div className="hidden lg:block">
                <div className="sticky top-24 bg-white border border-[#E5E7EB] rounded-xl p-6 shadow-sm" data-testid="cta-card-desktop">
                  <h3 className="text-lg font-semibold text-[#111827] mb-4">Get Started</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => document.getElementById('programs-section')?.scrollIntoView({ behavior: 'smooth' })}
                      data-testid="explore-programs-button-desktop"
                      className="w-full px-4 py-3 bg-[#F97316] hover:bg-[#fb7e2e] text-white font-semibold rounded-lg transition-all duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                    >
                      Explore Programs
                    </button>
                    <button
                      onClick={handleConsultClick}
                      data-testid="consult-us-button-desktop"
                      className="w-full px-4 py-3 bg-white border-2 border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white font-semibold rounded-lg transition-all duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                    >
                      Consult Us
                    </button>
                    <button
                      onClick={handleBookTourClick}
                      data-testid="book-campus-tour-button-desktop"
                      className="w-full px-4 py-3 bg-white border border-[#E5E7EB] text-[#111827] hover:bg-[#F8FAFC] font-medium rounded-lg transition-all duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                    >
                      Book Campus Tour
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mobile CTAs */}
            <div className="lg:hidden mt-8 grid grid-cols-2 gap-3 px-2" data-testid="cta-buttons-mobile">
              <button
                onClick={() => document.getElementById('programs-section')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="explore-programs-button-mobile"
                className="px-4 py-3 bg-[#F97316] hover:bg-[#fb7e2e] text-white font-semibold rounded-lg transition-all duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
              >
                Explore Programs
              </button>
              <button
                onClick={handleConsultClick}
                data-testid="consult-us-button-mobile"
                className="px-4 py-3 bg-white border-2 border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white font-semibold rounded-lg transition-all duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
              >
                Consult Us
              </button>
            </div>
          </div>
        </section>

        {/* Subtle divider */}
        <div className="border-t border-[#E5E7EB]"></div>

        {/* SECTION 2: QUICK FACTS BAR */}
        <section className="py-8 md:py-10 bg-[#F8FAFC]" data-testid="quick-facts-section">
          <div className="container mx-auto px-6 lg:px-10 max-w-[1100px]">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 md:p-5 text-center hover:shadow-sm transition-shadow duration-200" data-testid="fact-card-type">
                <div className="text-[#6B7280] mb-2">
                  <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="text-xs text-[#6B7280] mb-1">Type</p>
                <p className="text-sm font-semibold text-[#111827]">{university.institution_type}</p>
              </div>
              
              <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 md:p-5 text-center hover:shadow-sm transition-shadow duration-200" data-testid="fact-card-levels">
                <div className="text-[#6B7280] mb-2">
                  <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <p className="text-xs text-[#6B7280] mb-1">Levels</p>
                <p className="text-sm font-semibold text-[#111827]">{university.levels_offered?.length || 0}</p>
              </div>
              
              <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 md:p-5 text-center hover:shadow-sm transition-shadow duration-200" data-testid="fact-card-programs">
                <div className="text-[#6B7280] mb-2">
                  <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-xs text-[#6B7280] mb-1">Programs</p>
                <p className="text-sm font-semibold text-[#111827]">{programs.length}</p>
              </div>
              
              <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 md:p-5 text-center hover:shadow-sm transition-shadow duration-200" data-testid="fact-card-location">
                <div className="text-[#6B7280] mb-2">
                  <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                </div>
                <p className="text-xs text-[#6B7280] mb-1">Location</p>
                <p className="text-sm font-semibold text-[#111827]">{university.city}</p>
              </div>
              
              {(university.qs_ranking || university.setara_rating) && (
                <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 md:p-5 text-center hover:shadow-sm transition-shadow duration-200" data-testid="fact-card-ranking">
                  <div className="text-[#F97316] mb-2">
                    <svg className="w-6 h-6 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                  <p className="text-xs text-[#6B7280] mb-1">Ranking</p>
                  <p className="text-sm font-semibold text-[#111827]">
                    {university.qs_ranking ? `#${university.qs_ranking}` : `${university.setara_rating}★`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* SECTION 3: ABOUT */}
        <section className="py-12 md:py-16 bg-white" data-testid="about-section">
          <div className="container mx-auto px-6 lg:px-10 max-w-[1100px]">
            <h2 className="text-2xl md:text-3xl text-[#111827] mb-8 font-bold text-center md:text-left">About the University</h2>
            
            <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl p-6 md:p-8">
              <p className="text-[#4B5563] mb-6 leading-relaxed text-base">
                {university.short_description || 'No description available.'}
              </p>
              
              {/* Key Highlights */}
              {university.fields_of_study && university.fields_of_study.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#111827] mb-4">Key Highlights</h3>
                  <ul className="space-y-3">
                    {university.fields_of_study.slice(0, 5).map((field, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-[#4B5563]">
                        <svg className="w-5 h-5 text-[#F97316] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>Programs in {field}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Accreditations */}
              {university.accreditations && university.accreditations.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-[#111827] mb-4">Accreditations & Recognitions</h3>
                  <div className="flex flex-wrap gap-2">
                    {university.accreditations.slice(0, 5).map((acc, idx) => (
                      <span key={idx} className="px-3 py-2 bg-white text-[#4B5563] rounded-lg text-sm border border-[#E5E7EB]">
                        {acc}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Subtle divider */}
        <div className="border-t border-[#E5E7EB]"></div>

        {/* SECTION 4: PROGRAMS OFFERED */}
        {programs.length > 0 && (
          <section id="programs-section" className="py-12 md:py-16 bg-[#F8FAFC]" data-testid="programs-section">
            <div className="container mx-auto px-6 lg:px-10 max-w-[1100px]">
              {/* Mobile Collapsible Header */}
              <div className="md:hidden mb-6">
                <button
                  onClick={() => setProgramsSectionExpanded(!programsSectionExpanded)}
                  className="w-full flex items-center justify-between py-4 px-5 bg-white border border-[#E5E7EB] rounded-xl"
                  data-testid="programs-section-toggle"
                >
                  <h2 className="text-xl font-bold text-[#111827]">Programs Offered</h2>
                  <svg 
                    className={`w-6 h-6 text-[#6B7280] transition-transform duration-300 ${programsSectionExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Desktop Header */}
              <h2 className="hidden md:block text-2xl md:text-3xl text-[#111827] mb-8 font-bold">Programs Offered</h2>
              
              {/* Content (Collapsible on Mobile) */}
              <div className={`${!programsSectionExpanded ? 'hidden md:block' : 'block'}`}>
                {/* MAIN TABS - Level of Study */}
                {levels.length > 0 && (
                  <div className="mb-6 overflow-x-auto" data-testid="program-level-tabs">
                    <div className="flex gap-2 border-b border-[#E5E7EB] pb-4 min-w-max">
                      {levels.map(level => (
                        <button
                          key={level}
                          onClick={() => {
                            setSelectedProgramLevel(level);
                            setSelectedField('');
                            setShowAllPrograms(false);
                          }}
                          data-testid={`program-tab-${level.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E40AF] whitespace-nowrap ${
                            selectedProgramLevel === level 
                              ? 'bg-[#F97316] text-white shadow-sm' 
                              : 'bg-white text-[#4B5563] hover:bg-[#F8FAFC] border border-[#E5E7EB]'
                          }`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* SUB-TABS - Field of Study */}
                {fieldsForLevel.length > 0 && (
                  <div className="mb-8 overflow-x-auto" data-testid="program-field-tabs">
                    <div className="flex gap-2 min-w-max">
                      {fieldsForLevel.map(field => (
                        <button
                          key={field}
                          onClick={() => {
                            setSelectedField(field);
                            setShowAllPrograms(false);
                          }}
                          data-testid={`field-tab-${field.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E40AF] whitespace-nowrap ${
                            selectedField === field 
                              ? 'bg-[#F97316] text-white' 
                              : 'bg-white text-[#4B5563] hover:bg-[#E5E7EB] border border-[#E5E7EB]'
                          }`}
                        >
                          {field}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Program Cards */}
                {filteredPrograms.length > 0 && !showAllPrograms && (
                  <div className="mb-8">
                    {/* 4-Column Carousel */}
                    <AutoCarousel autoPlayInterval={5000}>
                      {filteredPrograms.map(program => (
                        <div key={program.id} className="bg-white border border-[#E5E7EB] rounded-xl p-5 h-full hover:border-[#F97316] hover:shadow-md transition-all duration-200" data-testid="program-card">
                          <h3 className="text-[#111827] mb-4 line-clamp-2" style={{ fontSize: '1.1rem', fontWeight: 650, minHeight: '3rem' }}>{program.program_name}</h3>
                          <div className="space-y-3 mb-4 text-sm text-[#4B5563]">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-xs">{program.duration}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-xs">{program.mode_of_study}</span>
                            </div>
                            
                            {program.estimated_fee_local && (
                              <div className="pt-3 border-t border-[#E5E7EB]">
                                <p className="text-xs text-[#6B7280] mb-1">Local Student Fee</p>
                                <p className="text-sm font-semibold text-[#111827]">RM {program.estimated_fee_local.toLocaleString()}</p>
                              </div>
                            )}
                          </div>
                          
                          {program.cashback_amount && (
                            <div className="mb-4">
                              <span className="inline-block px-3 py-1.5 bg-[#D1FAE5] text-[#065F46] text-xs font-medium rounded-md">
                                Cashback: RM {program.cashback_amount}
                              </span>
                            </div>
                          )}
                          
                          <div className="flex gap-2 mt-auto pt-4">
                            <button 
                              onClick={() => navigate(`/programs/${program.slug || program.id}`)}
                              data-testid="program-view-details-button"
                              className="flex-1 px-3 py-2 bg-[#EFF6FF] hover:bg-[#DBEAFE] text-[#1E40AF] text-xs font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E40AF] flex items-center justify-center gap-1"
                            >
                              View Details
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            <button
                              onClick={handleConsultClick}
                              data-testid="program-consult-button"
                              className="flex-1 px-3 py-2 bg-[#F97316] hover:bg-[#fb7e2e] text-white text-xs font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                            >
                              Consult
                            </button>
                          </div>
                        </div>
                      ))}
                    </AutoCarousel>
                  </div>
                )}
                
                {/* Full List View */}
                {showAllPrograms && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {filteredPrograms.map(program => (
                      <div key={program.id} className="bg-white border border-[#E5E7EB] rounded-xl p-5 hover:border-[#F97316] hover:shadow-md transition-all duration-200" data-testid="program-card">
                        <h3 className="text-[#111827] mb-4 line-clamp-2" style={{ fontSize: '1.2rem', fontWeight: 650, minHeight: '3rem' }}>{program.program_name}</h3>
                        <div className="space-y-3 mb-4 text-sm text-[#4B5563]">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{program.duration}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>{program.mode_of_study}</span>
                          </div>
                          
                          {program.estimated_fee_local && (
                            <div className="pt-3 border-t border-[#E5E7EB]">
                              <p className="text-xs text-[#6B7280] mb-1">Local Student Fee</p>
                              <p className="text-base font-semibold text-[#111827]">RM {program.estimated_fee_local.toLocaleString()}</p>
                            </div>
                          )}
                          
                          {program.estimated_fee_international && (
                            <div className="pt-2">
                              <p className="text-xs text-[#6B7280] mb-1">International Student Fee</p>
                              <p className="text-base font-semibold text-[#111827]">RM {program.estimated_fee_international.toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                        
                        {program.cashback_amount && (
                          <div className="mb-4">
                            <span className="inline-block px-3 py-1.5 bg-[#D1FAE5] text-[#065F46] text-xs font-medium rounded-md">
                              Cashback: RM {program.cashback_amount}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex gap-2 mt-auto pt-4">
                          <button 
                            data-testid="program-view-details-button"
                            className="flex-1 px-3 py-2 bg-[#F8FAFC] hover:bg-[#E5E7EB] text-[#111827] text-xs font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                          >
                            View Details
                          </button>
                          <button
                            onClick={handleConsultClick}
                            data-testid="program-consult-button"
                            className="flex-1 px-3 py-2 bg-[#F97316] hover:bg-[#fb7e2e] text-white text-xs font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                          >
                            Consult
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* View All Programs Button */}
                {currentLevelPrograms.filter(p => (p.field_of_study || 'General') === selectedField).length > 6 && (
                  <div className="text-center">
                    <button
                      onClick={() => setShowAllPrograms(!showAllPrograms)}
                      data-testid="view-all-programs-button"
                      className="px-6 py-3 text-sm font-medium text-[#F97316] hover:text-[#fb7e2e] border border-[#F97316] hover:border-[#fb7e2e] rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                    >
                      {showAllPrograms ? 'Show Less' : 'View All →'}
                    </button>
                  </div>
                )}
                
                {filteredPrograms.length === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl border border-[#E5E7EB]" data-testid="programs-empty-state">
                    <p className="text-[#6B7280]">No programs available for this selection.</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* SECTION 5: SCHOLARSHIPS */}
        {scholarships.length > 0 && (
          <section className="py-12 md:py-16 bg-white" data-testid="scholarships-section">
            <div className="container mx-auto px-6 lg:px-10 max-w-[1100px]">
              {/* Mobile Collapsible Header */}
              <div className="md:hidden mb-6">
                <button
                  onClick={() => setScholarshipsSectionExpanded(!scholarshipsSectionExpanded)}
                  className="w-full flex items-center justify-between py-4 px-5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl"
                  data-testid="scholarships-section-toggle"
                >
                  <h2 className="text-xl font-bold text-[#111827]">Scholarships & Financial Aid</h2>
                  <svg 
                    className={`w-6 h-6 text-[#6B7280] transition-transform duration-300 ${scholarshipsSectionExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Desktop Header */}
              <h2 className="hidden md:block text-2xl md:text-3xl text-[#111827] mb-8 font-bold">Scholarships & Financial Aid</h2>
              
              {/* Content (Collapsible on Mobile) */}
              <div className={`${!scholarshipsSectionExpanded ? 'hidden md:block' : 'block'}`}>
                {/* 4-Column Carousel */}
                <div className="mb-8">
                  <AutoCarousel autoPlayInterval={6000}>
                    {displayedScholarships.map(scholarship => (
                      <div key={scholarship.id} className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl p-5 h-full hover:border-[#F97316] hover:shadow-md transition-all duration-200" data-testid="scholarship-card">
                        <h3 className="text-sm font-semibold text-[#111827] mb-4 line-clamp-2" style={{ minHeight: '2.5rem' }}>{scholarship.name}</h3>
                        <div className="space-y-2 mb-4 text-xs text-[#4B5563]">
                          <p><span className="font-medium text-[#111827]">Value:</span> {scholarship.value_amount}</p>
                          <p><span className="font-medium text-[#111827]">Type:</span> {scholarship.scholarship_type}</p>
                          {scholarship.eligible_levels && scholarship.eligible_levels.length > 0 && (
                            <p><span className="font-medium text-[#111827]">Eligible:</span> {scholarship.eligible_levels.slice(0,2).join(', ')}</p>
                          )}
                        </div>
                        <button
                          onClick={() => navigate(`/scholarships/${scholarship.slug || scholarship.id}`)}
                          data-testid="scholarship-learn-more-button"
                          className="w-full px-3 py-2 bg-[#EFF6FF] hover:bg-[#DBEAFE] text-[#1E40AF] text-xs font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-1"
                        >
                          Learn More
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </AutoCarousel>
                </div>
                
                {/* View All Scholarships Button */}
                {scholarships.length > 6 && (
                  <div className="text-center mb-8">
                    <button
                      onClick={() => setShowAllScholarships(!showAllScholarships)}
                      data-testid="view-all-scholarships-button"
                      className="px-6 py-3 text-sm font-medium text-[#F97316] hover:text-[#fb7e2e] border border-[#F97316] hover:border-[#fb7e2e] rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                    >
                      {showAllScholarships ? 'Show Less' : 'View All →'}
                    </button>
                  </div>
                )}
                
                <div className="text-center">
                  <button
                    onClick={handleConsultClick}
                    data-testid="check-scholarship-eligibility-button"
                    className="px-6 py-3 bg-[#F97316] hover:bg-[#fb7e2e] text-white font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                  >
                    Check Scholarship Eligibility
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Subtle divider */}
        <div className="border-t border-[#E5E7EB]"></div>

        {/* SECTION 6: FINAL ADVISORY CTA */}
        <section className="py-16 md:py-20 bg-[#F8FAFC]" data-testid="final-cta-section">
          <div className="container mx-auto px-6 lg:px-10 max-w-[1100px]">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              {/* Left: Reassurance Copy */}
              <div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl text-[#111827] mb-6 font-bold leading-tight text-center md:text-left">
                  Ready to Make an Informed Decision?
                </h2>
                <p className="text-[#4B5563] mb-6 leading-relaxed text-base text-center md:text-left">
                  Our education advisors are here to guide you through program selection, scholarship applications, and cost optimization—ensuring you make the best choice for your future.
                </p>
                <ul className="space-y-3 text-sm text-[#4B5563]">
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-[#F97316] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Personalized program recommendations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-[#F97316] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Scholarship and financial aid guidance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-[#F97316] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Application support and admission assistance</span>
                  </li>
                </ul>
              </div>
              
              {/* Right: CTA Box */}
              <div className="bg-white border-2 border-[#F97316] rounded-xl p-8 shadow-sm" data-testid="final-cta-box">
                <h3 className="text-xl font-semibold text-[#111827] mb-6 text-center">Get Expert Guidance</h3>
                <div className="space-y-3">
                  <button
                    onClick={handleConsultClick}
                    data-testid="consult-advisor-button"
                    className="w-full px-6 py-4 bg-[#F97316] hover:bg-[#fb7e2e] text-white font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                  >
                    Consult Unisave Advisor
                  </button>
                  <button
                    onClick={handleBookTourClick}
                    data-testid="book-campus-tour-button-final"
                    className="w-full px-6 py-4 bg-white border-2 border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                  >
                    Book Campus Tour
                  </button>
                </div>
                <p className="text-xs text-[#6B7280] text-center mt-4">No obligations. Free consultation.</p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: USP IMAGES */}
        <section className="py-12 md:py-16 bg-white" data-testid="usp-section">
          <div className="container mx-auto px-6 lg:px-10 max-w-[1100px]">
            {/* Desktop: Single Row */}
            <div className="hidden md:grid md:grid-cols-6 gap-4">
              {uspItems.map((item, idx) => (
                <div key={idx} className="relative aspect-square rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                  <img 
                    src={item.imageUrl} 
                    alt={item.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>

            {/* Mobile: 2 columns × 3 rows grid */}
            <div className="md:hidden grid grid-cols-2 gap-4">
              {uspItems.map((item, idx) => (
                <div key={idx} className="relative aspect-square rounded-xl overflow-hidden shadow-sm">
                  <img 
                    src={item.imageUrl} 
                    alt={item.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <WhatsAppCTA phone="60123456789" message="Hi, I would like to consult about university programs." />
      
      {/* Appointment Form Modal */}
      {showAppointmentForm && (
        <AppointmentForm 
          onClose={handleAppointmentFormClose}
          onSubmit={handleAppointmentSubmit}
        />
      )}
    </div>
  );
};

export default UniversityDetail;
